import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import type { NextPageWithLayout, Product } from '@/types';
import { motion } from 'framer-motion';
import Layout from '@/layouts/_layout';
import client from '@/data/client';
import ProductSocialShare from '@/components/product/product-social-share';
import ProductDetailsPaper from '@/components/product/product-details-paper';
import {
  fadeInBottom,
} from '@/lib/framer-motion/fade-in-bottom';
import ProductQuestions from '@/components/questions/product-questions';
import isEmpty from 'lodash/isEmpty';
import invariant from 'tiny-invariant';
import ProductDetailsWidget from '@/components/product/product-details-widget';
import { useProducts } from '@/data/product';
import Grid from '@/components/product/grid';
import ProductReviewWidget from '@/components/product/product-review-widget';
import { useEffect } from 'react';
import { trackEvent } from '@/data/utils/utils';
import { ProductJsonLd } from 'next-seo';
import Seo from '@/layouts/_seo';
import Generate from '@/components/models/generate';
import Breadcrumb from '@/components/navigation/BreadCrumb';

// This function gets called at build time
type ParsedQueryParams = {
  productSlug: string;
};

export const getStaticPaths: GetStaticPaths<ParsedQueryParams> = async ({
  locales,
}) => {
  invariant(locales, 'locales is not defined');
  const { data } = await client.products.allBackend({
    limit: Number.MAX_VALUE,
  });
  const paths = data?.flatMap((product) =>
    locales?.map((locale) => ({
      params: { productSlug: product.slug },
      locale,
    }))
  );
  return {
    paths,
    fallback: 'blocking',
  };
};

type PageProps = {
  product: Product;
};

export const getStaticProps: GetStaticProps<
  PageProps,
  ParsedQueryParams
> = async ({ params, locale }) => {
  const { productSlug } = params!; //* we know it's required because of getStaticPaths
  try {
    const product = await client.products.get({
      slug: productSlug,
      language: locale,
    });
    return {
      props: {
        product,
        ...(await serverSideTranslations(locale!, ['common'])),
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    //* if we get here, the product doesn't exist or something else went wrong
    return {
      notFound: true,
    };
  }
};

function getPreviews(gallery: any[], image: any) {
  if (!isEmpty(gallery) && Array.isArray(gallery)) return gallery;
  if (!isEmpty(image)) return [image, {}];
  return [{}, {}];
}

const ProductPage: NextPageWithLayout<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ product }) => {
  const { t } = useTranslation('common');
  const { id, name, slug, image, gallery } = product;
  const is_custom_ai_model =
    product.ai_model_version.slug === 'lora-general-training';
  const {
    products: similarProducts,
    loadMore,
    hasNextPage,
    isLoadingMore,
    isLoading,
  } = useProducts(
    {
      sortedBy: 'DESC',
      orderBy: 'orders_count',
      name,
      limit: 4,
      exclude: parseInt(id, 10),
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    trackEvent('view_item', product);
  }, []);

  if (is_custom_ai_model) {
    return <Generate product={product} />;
  }

  return (
    <>
      <Seo
        title={`${product.name} | ${product.ai_model.name} Prompt`}
        description={product.description}
        url={`/products/${product.slug}`}
        canonical={`https://promptsclub.com/products/${product.slug}`}
        twitter={{
          handle: '@PromptsClub',
          site: '@PromptsClub',
          cardType: 'summary_large_image',
        }}
        openGraph={{
          type: 'website',
          url: `https://promptsclub.com/products/${product.slug}`,
          title: product.name,
          description: product.description,
          images: [
            {
              url: product.image?.original ?? '',
              alt: `${product.ai_model.name} Prompt for ${product.name} Thumbnail`,
              width: 500,
              height: 500,
            },
            ...gallery.map((img, index) => ({
              url: img.original,
              alt: `${product.ai_model.name} Prompt for ${
                product.name
              } Image Example ${index + 1}`,
              width: 500,
              height: 500,
            })),
          ],
        }}
      />
      <ProductJsonLd
        productName={`${product.ai_model.name} Prompt for ${product.name}`}
        images={[
          product.image?.original ?? '',
          ...gallery.map((img) => img.original),
        ]}
        description={product.description}
        // TODO: keywords={product.thumbnail_text.replace()}
        keywords={`${product.ai_model.name},${product.thumbnail_text}`}
        // sku={product.sku}
        // mpn={product.mpn}
        // brand={product.brand}
        offers={{
          price: product.price,
          priceCurrency: 'USD',
          url: `https://promptsclub.com/products/${product.slug}`,
          availability: 'http://schema.org/InStock',
          itemCondition: 'http://schema.org/NewCondition',
          aggregateRating: {
            ratingValue: product.store_review_score,
            reviewCount: product.store_review_amount,
          },
          seller: {
            name: product.shop?.name,
          },
        }}
      />
      <div className="relative">
        <div className="h-full min-h-screen p-4 md:px-6 lg:px-8 lg:pt-6">
          <div className="sticky top-0 z-20 -mx-4 mb-1 -mt-2 flex items-center bg-white p-4 dark:bg-black sm:static sm:top-auto sm:z-0 sm:m-0 sm:mb-4 sm:bg-transparent sm:p-0 sm:dark:bg-transparent">
            <Breadcrumb />
          </div>
          <div className="flex justify-center">
            <ProductDetailsWidget productSSR={product} productSlug={slug} />
          </div>
          <motion.div
            variants={fadeInBottom()}
            className="justify-center py-6 lg:flex lg:flex-col lg:py-10"
          >
            <div className="mt-4 w-full md:mt-8 md:space-y-10 lg:mt-12 lg:hidden lg:flex-col lg:space-y-12">
              <ProductReviewWidget product={product} />
              <ProductQuestions
                productId={product?.id}
                shopId={product?.shop?.id}
                className="hidden"
              />
            </div>
            <ProductSocialShare
              productSlug={slug}
              className="hidden border-t border-light-500 pt-5 dark:border-dark-400 md:pt-7"
            />
            <div className="mx-auto w-full max-w-6xl">
              <h2 className="w-full border-b border-light-500 pb-2 pt-5 text-lg tracking-tight text-dark dark:border-dark-400 dark:text-light">
                You may also like
              </h2>
              <div className="search-results -mx-4 flex min-h-[84%] flex-col px-4 md:-mx-6 lg:-mx-7 lg:py-2 xl:px-20 3xl:-mx-8">
                <Grid
                  products={similarProducts}
                  onLoadMore={loadMore}
                  hasNextPage={hasNextPage}
                  isLoadingMore={isLoadingMore}
                  isLoading={isLoading}
                  limit={4}
                  disableAutoLoad
                />
              </div>
            </div>
          </motion.div>
        </div>
        <div className="product-details-wrapper">
          <ProductDetailsPaper product={product} />
        </div>
      </div>
    </>
  );
};

ProductPage.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default ProductPage;
